import React, { useState } from "react";
import { X, MapPin, Phone } from "lucide-react";
import type { Location } from "./MenuModals";
import { formatTime12h, getTimezoneAbbreviation } from "../../lib/utils";

type Slot = { start: string; end: string };
type Timeline = { day: string; isOpen: boolean; slots: Slot[] };
type TabKey = "store" | "pickup" | "delivery";

const tabs: { key: TabKey; label: string }[] = [
  { key: "store", label: "Store" },
  { key: "pickup", label: "Pickup" },
  { key: "delivery", label: "Delivery" },
];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  location: Location | null;
}

const TimelineBlock = ({
  title,
  timeline,
  timezone,
}: {
  title: string;
  timeline?: Timeline[];
  timezone: string;
}) => {
  if (!timeline) return null;

  const tzAbbr = getTimezoneAbbreviation(timezone);

  return (
    <div className="space-y-3">
      <h3 className="text-base sm:text-lg font-semibold text-black">
        {title} <span className="text-sm font-normal text-gray-600">({tzAbbr})</span>
      </h3>

      <div className="border border-black/10 rounded-xl divide-y divide-border/5">
        {timeline.map((day) => (
        <div
          key={day.day}
          className="flex justify-between px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm"
        >
          <span className="font-medium whitespace-nowrap text-black">
            {day.day}
          </span>

          {!day.isOpen || day.slots.length === 0 ? (
            <span className="text-gray-400">Closed</span>
          ) : (
            <span className="text-gray-700 text-right break-words">
              {day.slots
                .map((s) => `${formatTime12h(s.start)} – ${formatTime12h(s.end)}`)
                .join(" and ")}
            </span>
          )}
        </div>
        ))}
      </div>
    </div>
  );
};

export function HotelDetailsModal({ isOpen, onClose, location }: Props) {
 const [activeTab, setActiveTab] = useState<
    "store" | "pickup" | "delivery"
  >("store");
  if (!isOpen || !location) return null;

  // Get the hotel's timezone, fallback to America/Denver (MST) if not provided
  const hotelTimezone = location.timezone || location._timezoneInfo?.id || "America/Denver";

  const mapSrc=`https://www.google.com/maps?q=${location.latitude},${location.longitude}&z=15&output=embed`;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="dm-order-modal bg-white text-black rounded-3xl w-full sm:w-11/12 md:w-9/12 max-w-3xl max-h-[90vh] overflow-y-auto border border-black/10"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
     <div className="sticky top-0 bg-white px-4 sm:px-6 py-3 border-b border-black/5 flex justify-between items-center">
  <h2 className="text-xl sm:text-2xl font-bold text-black">
            {(location.hotelName ?? "").replace(/Hotel/gi, "").trim()}
          </h2>
          <button type="button" onClick={onClose} aria-label="Close">
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Content */}
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 text-black">
          {/* Map */}
          <div className="rounded-2xl overflow-hidden border border-black/10 h-48 sm:h-64">
            <iframe
              src={mapSrc}
              width="100%"
              height="100%"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
          {/* Address */}
        <div className="flex items-start gap-2 sm:gap-3 text-sm sm:text-base text-gray-700">
  <MapPin className="w-4 h-4 sm:w-5 sm:h-5 mt-1 flex-shrink-0" />
  <span className="break-words">{location.address}</span>
</div>

          {/* Phone */}
          {location.phoneNo && (
            <div className="flex items-center gap-2 sm:gap-3 text-sm sm:text-base text-gray-700">
  <Phone className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
  <span>{location.phoneNo}</span>
</div>  
          )}
          {/* Timelines */}
          {/* Tabs */}
<div className="flex gap-4 sm:gap-6 border-b border-black/10">
 {tabs.map((tab) => (
  <button
    key={tab.key}
    onClick={() => setActiveTab(tab.key)}
    className={`pb-2 sm:pb-3 text-xs sm:text-sm font-medium transition ${
      activeTab === tab.key
        ? "border-b-2 border-black text-black"
        : "text-gray-600"
    }`}
  >
    {tab.label}
  </button>
))}

</div>
{/* Timelines */}
{activeTab === "store" && (
  <TimelineBlock
    title="Store Hours"
    timeline={location.storeTimeline}
    timezone={hotelTimezone}
  />
)}

{activeTab === "pickup" && (
  <TimelineBlock
    title="Pickup Hours"
    timeline={location.pickupTimeline}
    timezone={hotelTimezone}
  />
)}

{activeTab === "delivery" && (
  <TimelineBlock
    title="Delivery Hours"
    timeline={location.deliveryTimeline}
    timezone={hotelTimezone}
  />
)}

        </div>
      </div>
    </div>
  );
}
